import { useState, useMemo } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import styles from './CustomReports.module.scss';
import { useQueryParams } from '../../../../hooks/misc';
import MTTable from '../../../../components/tables/MTable';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../../utilities/translator/translation_keys';
import {
  useTableComponents,
  CustomDateRangeFilter,
  AutocompleteFilter,
} from '../../../../components/tables/MTableComponents';
import { dateTypes, getDateRange } from '../../../../components/tables/table';
import { useURLParams } from '../../../../providers/URLParamProvider';
import { useCurrentUser } from '../../../../providers/UserProvider';
import { useNavigate } from 'react-router-dom';
import TablePageLayout from '../../../../components/layouts/TablePageLayout';
import { Text } from '../../../../components/typography';
import Icon from '../../../../components/Icon';
import { customColors } from '../../../../utilities';
import { useParticipantMutations } from '../../../../hooks/participant/participant_mutations';
import { useAttachmentMutations } from '../../../../hooks/attachments/attachment_mutations';
import { getLocalTime } from '../../../../utilities/time';
import TextWithIcon from '../../../../components/typography/TextWithIcon';
import {
  intervalOptions,
  reportStatusTypes,
} from '../../../../utilities/reports';
import { Chip, CircularProgress } from '@mui/material';
import { downloadPDF } from '../../../../utilities/pdf_export/misc';
import { format } from 'date-fns';
import { useLabels } from '../../../../providers/LabelProvider';
import { useWorkspace } from '../../../../providers/WorkspaceProvider';
import { useTemplates } from '../../../../providers/TemplateProvider';
import { useWorkspacePermissions } from '../../../../providers/WorkspacePermissionsProvider';
import { useReportPDFData } from './custom_reports';
import { showToast } from '../../../../graphql/cache/modal';
import { toastVariant } from '../../../../constants/misc';

const getPastReportsQuery = loader('./CustomReports.list.graphql');
const getRelatedReportsQuery = loader('./CustomReports.getRelated.graphql');
const addReportMutation = loader('./CustomReports.add.graphql');
const updateReportMutation = loader('./CustomReports.update.graphql');

const tabOptions = [
  { title: 'My Reports', key: 'personal' },
  { title: 'Team Reports', key: 'team' },
];

export default function CustomReports() {
  const {
    textCell,
    dateCell,
    userCell,
    userFilterOptions,
    labelCell,
    multiAvatarCell,
  } = useTableComponents();

  const { t } = useTranslation();
  const { user, company } = useCurrentUser();
  const { getParam, upsertParam } = useQueryParams();
  const { filters, page, pageSize, sort, cachedDateFilters } = useURLParams();
  const [selectedTab, setSelectedTab] = useState('personal');
  const [addReport] = useMutation(addReportMutation);
  const [updateReport] = useMutation(updateReportMutation);
  const { addAttachment, updateAttachment } = useAttachmentMutations();
  const navigate = useNavigate();
  const { addParticipant } = useParticipantMutations();
  const { models } = useLabels();
  const { fetchPDFData } = useReportPDFData();

  const [dateSelectors, setDateSelectors] = useState(() => {
    return dateTypes.reduce((acc, [field, minKey, maxKey]) => {
      const [min, max] = getDateRange(
        cachedDateFilters?.[field],
        getParam(field),
      );
      acc[minKey] = min;
      acc[maxKey] = max;
      return acc;
    }, {});
  });

  const allFilters = useMemo(
    () => [...filters, { field: 'type', operator: 'eq', value: ['CUSTOM'] }],
    [filters],
  );

  const {
    loading: loadingList,
    data: { reports = [], reportsCount = 0 } = {},
    previousData: { reportsCount: oldCount } = {},
  } = useQuery(getPastReportsQuery, {
    variables: {
      options: {
        filters: [
          ...(allFilters ?? []),
          {
            field: 'creatorId',
            operator: selectedTab === 'personal' ? 'eq' : 'ne',
            value: `${user.id}`,
          },
        ],
        sort,
        page,
        pageSize,
      },
    },
  });

  const [expanded, setExpanded] = useState({});
  const [history, setHistory] = useState({});
  const [loadingGroupUuids, setLoadingGroupUuids] = useState(new Set());
  const [fetchHistory] = useLazyQuery(getRelatedReportsQuery);

  const tableData = useMemo(
    () =>
      reports.map((report) => {
        const subRows = report.groupUuid
          ? history[report.groupUuid]
          : undefined;
        return subRows ? { ...report, subRows } : report;
      }),
    [reports, history],
  );

  const getRowCanExpand = (row) => {
    if (row.depth > 0) return false;
    const report = row.original;
    const selfComplete = report.status === 'COMPLETE' ? 1 : 0;
    return (report.completedOccurrenceCount ?? 0) - selfComplete > 0;
  };

  const fetchGroupHistory = (primary) => {
    if (
      !primary?.groupUuid ||
      history[primary.groupUuid] ||
      loadingGroupUuids.has(primary.groupUuid)
    )
      return;
    setLoadingGroupUuids((prev) => new Set(prev).add(primary.groupUuid));
    fetchHistory({ variables: { groupUuid: primary.groupUuid } })
      .then(({ data }) => {
        const rest = (data?.reports ?? []).filter((r) => r.id !== primary.id);
        if (data?.historyCount > (data?.reports?.length ?? 0)) {
          showToast({
            title: 'Warning',
            message: `Report group has ${data.historyCount} occurrences; only the most recent ${data.reports.length} were loaded.`,
            variant: toastVariant.warning,
          });
        }
        setHistory((h) => ({ ...h, [primary.groupUuid]: rest }));
      })
      .finally(() => {
        setLoadingGroupUuids((prev) => {
          const next = new Set(prev);
          next.delete(primary.groupUuid);
          return next;
        });
      });
  };

  const handleExpandedChange = (updaterOrValue) => {
    setExpanded((old) => {
      let next =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(old)
          : updaterOrValue;
      const newlyExpandedIds =
        next === true
          ? tableData.map((r) => String(r.id))
          : Object.keys(next).filter((id) => next[id] && !old[id]);

      newlyExpandedIds.forEach((rowId) => {
        const primary = tableData.find((r) => String(r.id) === rowId);
        fetchGroupHistory(primary);
      });

      if (next === true) {
        next = tableData.reduce(
          (acc, r) => ({ ...acc, [String(r.id)]: true }),
          {},
        );
      }
      return next;
    });
  };

  const muiExpandButtonProps = ({ row }) => {
    if (row.depth > 0) return {};
    const groupUuid = row.original.groupUuid;
    if (!groupUuid || !loadingGroupUuids.has(groupUuid)) return {};
    return {
      disabled: true,
      children: <CircularProgress size={14} />,
    };
  };
  const { allVehicles, allWorkspaces } = useWorkspace();
  const { allTemplates } = useTemplates();
  const { allUsers } = useWorkspacePermissions();
  const { labels: allLabels } = useLabels();
  const lookups = useMemo(
    () => ({
      allVehicles,
      allWorkspaces,
      allUsers,
      allLabels,
      allTemplates,
    }),
    [allLabels, allVehicles, allUsers, allWorkspaces, allTemplates],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'groupUuid',
        header: '',
        size: 75,
        Cell: ({ row, table }) => {
          const completeCount = row.original.completedOccurrenceCount ?? 0;
          const isSubRow = row.depth > 0;

          return (
            <div className={styles.topCell}>
              {!isSubRow ? (
                <Chip
                  label={completeCount}
                  sx={{ fontSize: 12 }}
                  size="small"
                />
              ) : (
                <div />
              )}
              {row.original.status === 'PAUSED' && (
                <Icon
                  hover
                  style={{ color: customColors.BLUE, fontSize: '1.2rem' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateReport({
                      variables: {
                        reportId: row.original.id,
                        status: 'PUBLISHED',
                      },
                    });
                  }}
                >
                  play_circle
                </Icon>
              )}
              {row.original.status === 'PUBLISHED' && (
                <Icon
                  hover
                  style={{ color: customColors.BLUE, fontSize: '1.2rem' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateReport({
                      variables: {
                        reportId: row.original.id,
                        status: 'PAUSED',
                      },
                    });
                  }}
                >
                  pause_circle
                </Icon>
              )}
              {row.original.status === 'COMPLETE' && (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <Icon
                    hover
                    style={{
                      fontSize: '1.2rem',
                      color: customColors.BLUE,
                      marginTop: '5px',
                    }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const report = row.original || {};
                      const fileName = `${report.title}-${format(
                        new Date(),
                        'yyyy-MM-dd',
                      )}`;
                      const {
                        tablePDF = {},
                        graphPDF = {},
                        statsPDF = {},
                      } = await fetchPDFData(report, models, lookups);

                      await downloadPDF({
                        fileName,
                        customReport: { ...report, models },
                        props: { tablePDF, graphPDF, statsPDF },
                        lookups,
                        company,
                      });
                    }}
                  >
                    download
                  </Icon>
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: t(keys.common.STATUS),
        grow: true,
        enableSorting: true,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const r = reportStatusTypes[value];
          return labelCell({
            name: r?.title || value,
            color: r?.color || customColors.SECONDARY,
          });
        },
      },
      {
        accessorKey: 'interval',
        header: 'Interval',
        size: 100,
        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          const isSubRow = row.depth > 0;
          return (
            <Text weight={isSubRow ? 'regular' : 'semiBold'} size="sm" noMargin>
              {intervalOptions[value]?.label ?? value}
            </Text>
          );
        },
      },
      {
        accessorKey: 'title',
        header: t(keys.common.TITLE),
        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          const isSubRow = row.depth > 0;
          return (
            <Text weight={isSubRow ? 'regular' : 'semiBold'} size="sm" noMargin>
              {value}
            </Text>
          );
        },
      },
      {
        accessorKey: 'nextRunAt',
        size: 200,
        header: 'Next Scheduled',
        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          const isSubRow = row.depth > 0;
          const unscheduled = !isSubRow && row.original.status !== 'PUBLISHED';
          const complete =
            row.original.status === 'COMPLETE' &&
            !isSubRow &&
            row.original.interval === 'ONCE';
          if (isSubRow && row.original.status === 'COMPLETE') return null;
          if (row.original.status === 'PAUSED')
            return (
              <div className={styles.flexRow}>
                <TextWithIcon
                  leftIcon="snooze"
                  noMargin
                  weight="semiBold"
                  size="sm"
                  iconProps={{
                    fontSize: '1.2rem',
                    color: customColors.ORANGE,
                  }}
                >
                  Notifications Paused
                </TextWithIcon>
              </div>
            );
          return (
            <div className={styles.flexRow}>
              <TextWithIcon
                weight={isSubRow ? 'regular' : 'semiBold'}
                size="sm"
                noMargin
                leftIcon={
                  unscheduled && !complete ? 'error_outlined' : 'check_circle'
                }
                iconProps={{
                  fontSize: '1.2rem',
                  color:
                    unscheduled && !complete
                      ? customColors.RED
                      : customColors.GREEN,
                }}
              >
                {complete
                  ? t(keys.common.COMPLETE)
                  : unscheduled
                  ? 'Not Scheduled'
                  : !!value
                  ? getLocalTime(value).format('dddd MMMM Do, YYYY')
                  : ''}
              </TextWithIcon>
              {!isSubRow && row.original.status === 'COMPLETE' ? (
                <Icon
                  hover
                  style={{ color: customColors.BLUE, fontSize: '1.2rem' }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    const r = row.original;
                    const { data } = await addReport({
                      variables: {
                        title: r.title,
                        description: r.description,
                        interval: r.interval,
                        type: 'CUSTOM',
                        groupUuid: r.groupUuid,
                      },
                    });
                    const newId = data?.addReport?.id;
                    if (!newId) return;
                    const newVersion = r.version + 1;
                    await updateReport({
                      variables: { reportId: newId, version: newVersion },
                    });
                    for (const a of r.attachments ?? []) {
                      const result = await addAttachment({
                        url: a.url,
                        content: a.content,
                        type: a.type,
                        description: a.description,
                        parentId: newId,
                        model: 'report',
                        version: newVersion,
                      });
                      const newAttachmentId = result?.data?.addAttachment?.id;
                      if (newAttachmentId && a.sortIndex != null) {
                        await updateAttachment({
                          id: newAttachmentId,
                          sortIndex: a.sortIndex,
                        });
                      }
                    }
                    await addParticipant({
                      userId: user.id,
                      parentId: newId,
                      parentModel: 'report',
                    });
                    navigate(`/report/${newId}`);
                  }}
                >
                  refresh
                </Icon>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: 'lastRunAt',
        header: 'Delivered On',
        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          const isSubRow = row.depth > 0;
          return (
            <Text weight={isSubRow ? 'regular' : 'semiBold'} size="sm" noMargin>
              {!!value ? getLocalTime(value).format('dddd MMMM Do, YYYY') : ''}
            </Text>
          );
        },
      },
      {
        accessorKey: 'participants',
        header: t(keys.common.RECIPIENTS),
        enableSorting: false,
        Cell: ({ cell }) => multiAvatarCell({ cell }),
        Filter: ({ column }) => (
          <AutocompleteFilter
            items={userFilterOptions('all')}
            column={column}
            filterBy={t(keys.common.RECIPIENTS)}
            type="user"
          />
        ),
      },
      {
        accessorKey: 'creator',
        header: t(keys.common.CREATOR),
        grow: true,
        enableSorting: false,
        Filter: ({ column }) => (
          <AutocompleteFilter
            items={userFilterOptions('all')}
            column={column}
            type="user"
            filterBy={t(keys.common.CREATOR)}
          />
        ),
        Cell: ({ cell }) => userCell({ cell }),
      },
      {
        accessorKey: 'description',
        header: t(keys.common.DESCRIPTION),
        grow: true,
        enableSorting: false,
        minSize: 250,
        Cell: ({ cell }) => textCell({ cell }),
      },
      {
        accessorKey: 'dateCreated',
        header: t(keys.common.DATE_CREATED),
        size: 275,
        Cell: ({ cell }) => dateCell({ cell }),
        Filter: ({ column }) => (
          <CustomDateRangeFilter
            column={column}
            minDate={dateSelectors.dateCreatedMin}
            maxDate={dateSelectors.dateCreatedMax}
            setMinDate={(value) =>
              setDateSelectors({ ...dateSelectors, dateCreatedMin: value })
            }
            setMaxDate={(value) =>
              setDateSelectors({ ...dateSelectors, dateCreatedMax: value })
            }
          />
        ),
      },
      {
        accessorKey: 'dateModified',
        header: t(keys.common.LAST_UPDATED),
        size: 275,
        Cell: ({ cell }) => dateCell({ cell }),
        Filter: ({ column }) => (
          <CustomDateRangeFilter
            column={column}
            minDate={dateSelectors.dateModifiedMin}
            maxDate={dateSelectors.dateModifiedMax}
            setMinDate={(value) =>
              setDateSelectors({ ...dateSelectors, dateModifiedMin: value })
            }
            setMaxDate={(value) =>
              setDateSelectors({ ...dateSelectors, dateModifiedMax: value })
            }
          />
        ),
      },
      {
        accessorKey: 'version',
        header: 'Version',
        size: 100,
        Cell: ({ cell }) => textCell({ cell }),
      },
    ],
    [
      t,
      labelCell,
      textCell,
      dateCell,
      userFilterOptions,
      userCell,
      dateSelectors,
      multiAvatarCell,
      company,
      fetchPDFData,
      lookups,
      models,
      addReport,
      updateReport,
      addAttachment,
      updateAttachment,
      addParticipant,
      navigate,
      user.id,
    ],
  );

  return (
    <div className={styles.container}>
      <TablePageLayout
        header="Analytics"
        tabProps={{
          options: tabOptions,
          selected: selectedTab,
          setSelected: (t) => {
            setSelectedTab(t);
            upsertParam('tab', t);
          },
          navigation: false,
        }}
        buttonProps={{
          align: 'right',
          icon: 'add',
          value: t(keys.common.NEW),
          onClick: () => {
            addReport({
              variables: {
                title: 'Untitled Report',
                type: 'CUSTOM',
                participants: [user.id],
              },
            }).then(({ data }) => {
              const parentId = data?.addReport?.id;
              addParticipant({
                userId: user.id,
                parentId,
                parentModel: 'report',
              }).then(() => {
                navigate(`/report/${data?.addReport?.id}`);
              });
            });
          },
        }}
        table={
          <MTTable
            columns={columns}
            data={tableData}
            isLoading={loadingList}
            rowCount={reportsCount || oldCount}
            onRowClick={(report) => navigate(`/report/${report.id}`)}
            enableExpanding
            enableExpandAll
            expanded={expanded}
            onExpandedChange={handleExpandedChange}
            getRowCanExpand={getRowCanExpand}
            muiExpandButtonProps={muiExpandButtonProps}
            getSubRows={(row) => {
              return row.subRows;
            }}
          />
        }
      />
    </div>
  );
}
